import {
    Dialog,
    DialogContent,
    Button,
    IconButton,
    styled,
    Backdrop,
    Stack,
    DialogTitle,
    Alert,
    DialogActions,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form-v7';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import ConnectedDatePicker from './ConnectedDatePicker';
import usePatchProcessedTimesheet from '../../hooks/usePatchProcessedTimesheet';
import { useToast } from '../../Contexts/ToastContext';

const isFirstDayOfMonth = (date) => {
    const day = date.date();
    return day === 1;
};

const firstDayOfPreviousMonth = moment().subtract(1, 'month').startOf('month').startOf('day');
const firstDayOfCurrentMonth = moment().startOf('month').startOf('day');
const firstDayOfNextMonth = moment().add(1, 'month').startOf('month').startOf('day');
const lastDayOfNextMonth = moment().add(1, 'month').endOf('month').endOf('day');

const BackdropBlur = styled(Backdrop)({
    backdropFilter: 'blur(6px)',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
});

const initializeFormData = (data) => ({
    originalPayDate: data.originalPayDate ? moment(data.originalPayDate) : null,
    payrollDate: data.payrollDate ? moment(data.payrollDate) : null,
    submittedDate: data.submittedDate ? moment(data.submittedDate) : null,
    approvalDate: data.approvalDate ? moment(data.approvalDate) : null,
    weekEndingDate: data.weekEndingDate ? moment(data.weekEndingDate) : null,
    monthPosted: data.monthPostedDate ? moment(data.monthPostedDate) : null,
});

const UpdateHeaderDatesModal = ({ data, onSuccess, onClose }) => {
    const queryClient = useQueryClient();
    const { showErrorToast } = useToast();
    const { mutate, isLoading } = usePatchProcessedTimesheet({
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['processedTimesheets'] });
            queryClient.invalidateQueries({
                queryKey: [
                    'processedTimesheetDetails',
                    { processedTimesheetId: data.id.toString() },
                ],
            });
            onSuccess();
        },
        onError: (error) => {
            showErrorToast(error?.message || 'Failed to save. Please try again.');
        },
    });

    const {
        control,
        handleSubmit,
        formState: { isDirty, dirtyFields },
    } = useForm({ defaultValues: initializeFormData(data), mode: 'onBlur' });

    const onSubmit = (formData) => {
        const dirtyFieldsDict = {};
        Object.keys(dirtyFields).forEach((key) => {
            dirtyFieldsDict[key] = formData[key];
        });
        mutate({
            processedTimesheetId: data.id,
            lastModified: data.lastModified,
            ...dirtyFieldsDict,
        });
    };

    return (
        <Dialog
            open
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            slots={{ backdrop: BackdropBlur }}
            slotProps={{ paper: { sx: { borderRadius: '8px' } } }}
        >
            <DialogTitle sx={{ fontSize: '22px', fontWeight: '600', lineHeight: '120%' }}>
                Edit Dates
                <IconButton
                    title="Close"
                    onClick={onClose}
                    sx={{ position: 'absolute', top: 12, right: 12 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pb: 0 }}>
                <Stack component="form" id="header-dates-form" onSubmit={handleSubmit(onSubmit)}>
                    <ConnectedDatePicker
                        control={control}
                        name="originalPayDate"
                        pickerProps={{ label: 'Original Pay', readOnly: true }}
                    />
                    <ConnectedDatePicker
                        control={control}
                        name="payrollDate"
                        pickerProps={{
                            label: 'Payroll',
                            minDate: moment().add(1, 'day').startOf('day'),
                        }}
                        rules={{
                            validate: (selectedDate) => {
                                if (!dirtyFields.payrollDate) return true; // do not validate if field has no changes
                                if (!selectedDate) return 'Payroll Date cannot be cleared';

                                if (!selectedDate.isAfter(moment(), 'day')) {
                                    return 'Payroll Date must be a future date';
                                }
                                return true;
                            },
                        }}
                    />
                    <ConnectedDatePicker
                        control={control}
                        name="submittedDate"
                        pickerProps={{ label: 'Submitted', readOnly: true }}
                    />
                    <ConnectedDatePicker
                        control={control}
                        name="approvalDate"
                        pickerProps={{
                            label: 'Approval',
                            minDate: firstDayOfPreviousMonth,
                            maxDate: lastDayOfNextMonth,
                        }}
                        rules={{
                            validate: (selectedDate) => {
                                if (!dirtyFields.approvalDate) return true; // do not validate if field has no changes
                                if (!selectedDate) return 'Approval Date cannot be cleared';
                                if (
                                    !selectedDate?.isBetween(
                                        firstDayOfPreviousMonth,
                                        lastDayOfNextMonth
                                    )
                                ) {
                                    return 'Approval Date must be within first day of previous month and last day of next month';
                                }
                                return true;
                            },
                        }}
                    />
                    <ConnectedDatePicker
                        control={control}
                        name="weekEndingDate"
                        pickerProps={{ label: 'Week Ending', readOnly: true }}
                    />
                    <ConnectedDatePicker
                        control={control}
                        name="monthPosted"
                        pickerProps={{
                            label: 'Month Posted',
                            minDate: firstDayOfPreviousMonth,
                            maxDate: firstDayOfNextMonth,
                            shouldDisableDate: (date) => date && !isFirstDayOfMonth(date),
                        }}
                        rules={{
                            validate: (selectedDate) => {
                                if (!dirtyFields.monthPosted) return true; // do not validate if field has no changes
                                if (!selectedDate) return 'Month Posted Date cannot be cleared';

                                const selectedDateStart = selectedDate.startOf('day');
                                if (
                                    !(
                                        selectedDateStart.isSame(firstDayOfPreviousMonth) ||
                                        selectedDateStart.isSame(firstDayOfCurrentMonth) ||
                                        selectedDateStart.isSame(firstDayOfNextMonth)
                                    )
                                ) {
                                    return 'Month Posted Date can be first day of last, current or next month only';
                                }
                                return true;
                            },
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-start', px: 3, pt: 2, pb: '20px' }}>
                <Stack gap={2} width="100%">
                    {isDirty && <Alert severity="warning">You have unsaved changes.</Alert>}

                    <Stack direction="row" gap={1}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            size="small"
                            sx={{ minWidth: '50px' }}
                            disabled={isLoading}
                        >
                            {isDirty ? 'Discard changes' : 'Cancel'}
                        </Button>
                        <Button
                            form="header-dates-form"
                            disabled={!isDirty}
                            loading={isLoading}
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ minWidth: '50px' }}
                            type="submit"
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default UpdateHeaderDatesModal;
